import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemSearch, Modal } from '@/lib/components';
import { Button } from '@/lib/components/Button/Button';
import { FloatModal } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { ITag } from '@/src/ContactsModule/interfaces/Interfaces';

import { useTag } from '@/modules/ContactsModule/hooks/useTag';

const ButtonsSection = ({
  tags,
  setShowTagsModal,
  setSelectedTags,
  selectedTagsNames,
  setSelectedTagsNames,
  isUpdateCheckboxModal,
  setUnselectedTags,
}: {
  tags: ITag[];
  setShowTagsModal?: Dispatch<SetStateAction<boolean>>;
  setSelectedTags?: Dispatch<SetStateAction<number[]>>;
  selectedTagsNames: string[];
  setSelectedTagsNames: Dispatch<SetStateAction<string[]>>;
  isUpdateCheckboxModal: boolean;
  setUnselectedTags?: Dispatch<SetStateAction<number[]>>;
}) => {
  const { t } = useTranslation();
  const [disableButtons, setDisableButtons] = useState(false);

  useEffect(() => {
    !isUpdateCheckboxModal && setDisableButtons(selectedTagsNames.length === 0);
  }, [selectedTagsNames]);

  const cleanInputs = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkbox: any = document.getElementsByClassName('_modal_checkbox_'); // inputs in <ItemSearch/>
    if (checkbox) {
      for (let i = 0; i < checkbox.length; i++) {
        checkbox[i].checked = false;
      }
    }
  };

  return (
    <Flex withGap alignment="end" gapSize="medium">
      <Button
        stroked
        disabled={disableButtons}
        onAction={() => {
          cleanInputs();
          setSelectedTagsNames([]);
        }}
      >
        <div className="text-12 lg:text-12 xl:text-14 2xl:text-16">
          {t('CONTACT_TAG_MODAL.clearTag')}
        </div>
      </Button>
      <Button
        disabled={disableButtons}
        onAction={() => {
          const selectedTagsIds = tags
            .filter((g) => selectedTagsNames.includes(g.name))
            .map((g) => Number(g.id));

          const unselectedTagsIds = tags
            .filter((g) => !selectedTagsNames.includes(g.name))
            .map((g) => Number(g.id));

          setSelectedTags && setSelectedTags(selectedTagsIds);
          setUnselectedTags && setUnselectedTags(unselectedTagsIds);
          //closeModal();
          setShowTagsModal && setShowTagsModal(false);
        }}
      >
        <div className="text-12 lg:text-12 xl:text-14 2xl:text-16">{t('Save')}</div>
      </Button>
    </Flex>
  );
};

interface IContactsTagModalProps {
  contactTags?: ITag[];
  tags: ITag[];
  setSelectedTags?: Dispatch<SetStateAction<number[]>>;
  setUnselectedTags?: Dispatch<SetStateAction<number[]>>;
  setShowTagsModal?: Dispatch<SetStateAction<boolean>>;
  maxTagsToAdd?: number;
  isUpdateCheckboxModal?: boolean;
  setListSelectedTagsNames?: Dispatch<SetStateAction<string[]>>;
  setListUnselectedGroupsNames?: Dispatch<SetStateAction<string[]>>;
  onLastRefresh?: () => void;
}

export const ContactsTagModal = ({
  contactTags,
  tags,
  setShowTagsModal,
  setSelectedTags,
  setUnselectedTags,
  setListSelectedTagsNames,
  setListUnselectedGroupsNames,
  isUpdateCheckboxModal = false,
  onLastRefresh,
  maxTagsToAdd,
}: IContactsTagModalProps) => {
  const { t } = useTranslation();
  const [selectedTagsNames, setSelectedTagsNames] = useState<string[]>([]);
  const [unselectedTagsNames, setUnselectedTagsNames] = useState<string[]>([]);
  const [isLoadingTag, setIsLoadingTag] = useState(false);

  const { createTag } = useTag();

  const contactsID = contactTags && contactTags.map((e) => e.name);

  useEffect(() => {
    if (setListSelectedTagsNames && setListUnselectedGroupsNames) {
      setListSelectedTagsNames(selectedTagsNames);
      setListUnselectedGroupsNames(unselectedTagsNames);
    }
  }, [selectedTagsNames, unselectedTagsNames]);

  useEffect(() => {
    if (contactsID) {
      setSelectedTagsNames(contactsID);
    }
  }, []);

  useEffect(() => {
    if (contactsID) {
      setUnselectedTagsNames(contactsID);
    }
  }, []);

  const handleOnCreate = useCallback(
    async (value: string) => {
      setIsLoadingTag(true);
      const tagCreated = await createTag(value);
      setIsLoadingTag(false);
      if (tagCreated) onLastRefresh?.();
    },
    [createTag, onLastRefresh]
  );

  return (
    <FloatModal>
      <Modal>
        <div className="h-full p-8">
          {/* <Modal.Title onAction={() => closeModal()}> */}
          <Modal.Title onAction={() => setShowTagsModal && setShowTagsModal(false)}>
            <Flex withGap>
              <p>{t('CONTACT_TAG_MODAL.title')}</p>
              <p>({selectedTagsNames.length})</p>
            </Flex>
          </Modal.Title>
          <div className="h-[86%] sm:py-[2%] lg:py-[6%]">
            <Modal.Body withBorder>
              <div className="w-[30vw] p-[5%]">
                <ItemSearch
                  SearchPlaceHolder={t('CONTACT_TAG_MODAL.inputDescription')}
                  isLoading={isLoadingTag}
                  itemList={tags.map((tag) => tag.name)}
                  maxItems={maxTagsToAdd}
                  selectedItems={selectedTagsNames}
                  setSelectedItems={setSelectedTagsNames}
                  setUnselectedItems={setUnselectedTagsNames}
                  subTitle={t('CONTACT_TAG_MODAL.createTag')}
                  title={t('CONTACT_TAG_MODAL.chooseTag')}
                  onCreate={handleOnCreate}
                />
              </div>
            </Modal.Body>
          </div>
          <Modal.Footer>
            <ButtonsSection
              isUpdateCheckboxModal={isUpdateCheckboxModal}
              selectedTagsNames={selectedTagsNames}
              setSelectedTags={setSelectedTags}
              setSelectedTagsNames={setSelectedTagsNames}
              setShowTagsModal={setShowTagsModal}
              setUnselectedTags={setUnselectedTags}
              tags={tags}
            />
          </Modal.Footer>
        </div>
      </Modal>
    </FloatModal>
  );
};
